import React, { useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useSearchParams } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import RagClientSelect from '../../components/assistant/RagClientSelect';
import AssistantMessage from '../../components/assistant/AssistantMessage';
import AssistantSlashMenu from '../../components/assistant/AssistantSlashMenu';
import { AppPillSwitch, AppButton } from '../../components/ui';
import useAssistantChat, { ASSISTANT_MODES } from '../../hooks/useAssistantChat';
import { notify } from '../../components/common/AppToast';
import '../../styles/assistant.scss';
import '../../styles/assistant-slash.scss';
import '../../styles/kkp-planning-view-mode.scss';

const MODE_OPTIONS = [
  {
    value: ASSISTANT_MODES.question,
    label: 'Question',
    icon: 'mdi:chat-question-outline',
    hint: 'Poser une question sur les documents indexés',
  },
  {
    value: ASSISTANT_MODES.synthese,
    label: 'Synthèse client',
    icon: 'mdi:file-document-outline',
    hint: 'Générer une synthèse pour le client sélectionné',
  },
];

export default function AssistantPage() {
  const [searchParams] = useSearchParams();
  const initialClient = searchParams.get('client') || '';

  const {
    clients,
    clientsLoading,
    clientsError,
    selectedClient,
    setSelectedClient,
    mode,
    setMode,
    messages,
    input,
    setInput,
    sending,
    examples,
    syntheseDisabled,
    textareaRef,
    messagesEndRef,
    sendQuestion,
    handleRetry,
    slashOpen,
    slashItems,
    slashIndex,
    setSlashIndex,
    selectSlashSkill,
    handleComposerKeyDown,
  } = useAssistantChat({ initialClient });

  useEffect(() => {
    if (clientsError) {
      notify.error(clientsError);
    }
  }, [clientsError]);

  const handleSubmit = (event) => {
    event.preventDefault();
    sendQuestion(input);
  };

  const modeOptions = MODE_OPTIONS.map((option) => (
    option.value === ASSISTANT_MODES.synthese
      ? {
          ...option,
          disabled: syntheseDisabled,
          hint: syntheseDisabled
            ? 'Sélectionnez un client pour générer une synthèse'
            : option.hint,
        }
      : option
  ));

  return (
    <MasterLayout>
      <div className="assistant-layout">
        <div className="assistant-layout__shell card kkp-planning-results-card border-0 shadow-sm">
          <div className="kkp-planning-view-toolbar assistant-layout__toolbar-bar">
            <div className="assistant-layout__toolbar-grid">
              <div className="assistant-layout__client">
                <RagClientSelect
                  clients={clients}
                  value={selectedClient}
                  onChange={setSelectedClient}
                  loading={clientsLoading}
                  disabled={sending}
                />
              </div>
              <AppPillSwitch
                ariaLabel="Mode assistant"
                value={mode}
                onChange={setMode}
                disabled={sending}
                options={modeOptions}
              />
            </div>
          </div>

          <div className="assistant-layout__messages">
            {messages.length === 0 ? (
              <div className="assistant-layout__welcome text-center">
                <div className="assistant-layout__welcome-icon-wrap mb-8">
                  <Icon icon="mdi:robot-outline" className="assistant-layout__welcome-icon text-primary-600" />
                </div>
                <h6 className="fw-semibold mb-4">Posez une question sur vos clients</h6>
                <p className="text-secondary-light text-sm mb-12">
                  L&apos;assistant recherche dans les comptes rendus, briefs, bilans et contrats indexés.
                  Tapez <strong>/</strong> pour lancer un skill.
                </p>
                <div className="d-flex flex-column gap-8 align-items-center">
                  {examples.map((example) => (
                    <button
                      key={example}
                      type="button"
                      className="btn btn-outline-secondary btn-sm assistant-layout__example"
                      onClick={() => sendQuestion(example)}
                      disabled={sending}
                    >
                      <Icon icon="mdi:lightning-bolt-outline" className="me-1 flex-shrink-0" />
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="assistant-layout__thread">
                {messages.map((message) => (
                  <AssistantMessage
                    key={message.id}
                    message={message}
                    onRetry={message.error ? handleRetry : undefined}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <form className="assistant-layout__composer" onSubmit={handleSubmit}>
            <div className="assistant-layout__composer-inner">
              <AssistantSlashMenu
                open={slashOpen}
                items={slashItems}
                activeIndex={slashIndex}
                onHover={setSlashIndex}
                onSelect={selectSlashSkill}
              />
              <textarea
                ref={textareaRef}
                className="form-control assistant-layout__textarea"
                rows={1}
                placeholder={mode === ASSISTANT_MODES.synthese
                  ? 'Précisez éventuellement le focus de la synthèse…'
                  : 'Posez une question ou tapez / pour un skill…'}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleComposerKeyDown}
                disabled={sending}
              />
              <AppButton
                type="submit"
                variant="primary"
                className="flex-shrink-0 assistant-layout__send"
                disabled={sending || !input.trim()}
                loading={sending}
                icon="mdi:send"
                aria-label="Envoyer"
              />
            </div>
          </form>
        </div>
      </div>
    </MasterLayout>
  );
}
